// Soil moisture mapping
// Maps volumetric percentage values from API to descriptive soil moisture levels
// Based on agricultural soil moisture classification standards

export interface SoilMoistureOption {
  value: string;
  label: string;
  shortLabel: string;
  minPercent?: number;
  maxPercent?: number;
}

// Soil moisture options based on volumetric percentage ranges
export const SOIL_MOISTURE_OPTIONS: SoilMoistureOption[] = [
  {
    value: 'Dry',
    label: 'Dry',
    shortLabel: 'Dry',
    minPercent: 0,
    maxPercent: 15
  },
  {
    value: 'Moist',
    label: 'Moist',
    shortLabel: 'Moist',
    minPercent: 15.01,
    maxPercent: 35
  },
  {
    value: 'Wet',
    label: 'Wet',
    shortLabel: 'Wet',
    minPercent: 35.01,
    maxPercent: 100
  },
  {
    value: 'Unknown',
    label: 'Unknown',
    shortLabel: 'Unknown'
    // No percentage range for unknown
  }
];

/**
 * Convert volumetric percentage to soil moisture category
 * @param percentage - Soil moisture percentage (0-100) or undefined
 * @returns Soil moisture category string
 */
export const getsoilMoistureCategoryFromPercentage = (
  percentage: string | number | undefined
): string => {
  if (!percentage && percentage !== 0) return 'Unknown';

  const numericPercentage =
    typeof percentage === 'string' ? Number.parseFloat(percentage) : percentage;

  if (Number.isNaN(numericPercentage)) return 'Unknown';

  // Find the category that matches the percentage range
  const category = SOIL_MOISTURE_OPTIONS.find(option => {
    if (option.minPercent === undefined || option.maxPercent === undefined) {
      return false; // Skip options without percentage ranges (like Unknown)
    }
    return (
      numericPercentage >= option.minPercent &&
      numericPercentage <= option.maxPercent
    );
  });

  return category ? category.value : 'Unknown';
};

/**
 * Get soil moisture label from category value
 * @param value - Soil moisture category (e.g., "Dry", "Moist", "Wet")
 * @returns Formatted soil moisture label
 */
export const getSoilMoistureLabel = (value: string): string => {
  if (!value) return '';

  const option = SOIL_MOISTURE_OPTIONS.find(opt => opt.value === value);
  return option ? option.label : value; // Return original value if not found
};

/**
 * Get short soil moisture label from category value
 * @param value - Soil moisture category (e.g., "Dry", "Moist", "Wet")
 * @returns Short soil moisture label
 */
export const getSoilMoistureShortLabel = (value: string): string => {
  if (!value) return '';

  const option = SOIL_MOISTURE_OPTIONS.find(opt => opt.value === value);
  return option ? option.shortLabel : value; // Return original value if not found
};

/**
 * Get percentage range description for a soil moisture category
 * @param value - Soil moisture category (e.g., "Dry", "Moist", "Wet")
 * @returns Percentage range description
 */
export const getSoilMoisturePercentageRange = (value: string): string => {
  if (!value) return '';

  const option = SOIL_MOISTURE_OPTIONS.find(opt => opt.value === value);

  if (option?.minPercent === undefined || option?.maxPercent === undefined) {
    return '';
  }

  return `${option.minPercent}% - ${option.maxPercent}%`;
};
